import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import { Box, TextField, Select, Button } from "@mui/material";
import { toast } from "react-toastify";
import { transferirStockKae, getAlmacenes, getProductosKaeAlmacen } from "../../../../services";
import LaeButton from "../../../../components/Buttons/LaeButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "65ch",
  maxHeight: "80vh",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
};

const TransferirStock = ({ almacenOrigenId, opened, handleClose, onSaved }) => {
  const { t } = useTranslation(["common"]);
  const [almacenes, setAlmacenes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [formData, setFormData] = useState({
    producto_id: "",
    almacen_destino: "",
    cantidad: "",
  });
  const [stockDisponible, setStockDisponible] = useState(0);

  useEffect(() => {
    if (opened) {
      cargarDatos();
      setFormData({ producto_id: "", almacen_destino: "", cantidad: "" });
      setStockDisponible(0);
    }
  }, [opened]);

  const cargarDatos = async () => {
    try {
      const [almRes, prodRes] = await Promise.all([
        getAlmacenes(),
        getProductosKaeAlmacen(almacenOrigenId),
      ]);
      const almData = almRes?.data || almRes || [];
      setAlmacenes((Array.isArray(almData) ? almData : []).filter((a) => Number(a.id) !== Number(almacenOrigenId)));
      setProductos(Array.isArray(prodRes) ? prodRes : []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectProducto = (e) => {
    const productoId = parseInt(e.target.value);
    const producto = productos.find((p) => p.id === productoId);
    const stock = producto?.stocks?.[0]?.cantidad || 0;
    setStockDisponible(stock);
    setFormData({ ...formData, producto_id: productoId, cantidad: "" });
  };

  const handleSubmit = async () => {
    if (!formData.producto_id || !formData.almacen_destino || !formData.cantidad || formData.cantidad <= 0) {
      toast.error(t("almacenes.messages.emptyAlmacen"));
      return;
    }
    const toastInfo = toast.info(t("almacenes.transfer.saving"), { autoClose: false });
    try {
      const result = await transferirStockKae({
        producto_id: formData.producto_id,
        almacen_origen: Number(almacenOrigenId),
        almacen_destino: Number(formData.almacen_destino),
        cantidad: Number(formData.cantidad),
      });
      if (result.status === 200) {
        toast.update(toastInfo, {
          render: t("almacenes.transfer.success"),
          type: toast.TYPE.SUCCESS,
          autoClose: true,
        });
        if (onSaved) onSaved();
        else handleClose();
      } else {
        const msg = result?.response?.data?.error === "ERROR_STOCK_INSUFICIENTE"
          ? t("installations.messages.stockInsuficiente")
          : t("almacenes.transfer.error");
        toast.update(toastInfo, { render: msg, type: toast.TYPE.ERROR, autoClose: true });
      }
    } catch (err) {
      console.log(err);
      toast.update(toastInfo, { render: t("almacenes.transfer.error"), type: toast.TYPE.ERROR, autoClose: true });
    }
  };

  return (
    <Modal
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={opened}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box sx={style}>
        <div style={{ padding: "16px 32px 24px" }}>
          <h2>{t("almacenes.transfer.title")}</h2>

          <Box display="flex" flexDirection="column" gap={2} mb={3} mt={2}>
            <Select
              native
              value={formData.producto_id || ""}
              onChange={handleSelectProducto}
            >
              <option value="">{t("almacenes.transfer.selectProduct")}</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} (stock: {p.stocks?.[0]?.cantidad || 0})
                </option>
              ))}
            </Select>

            <Select
              native
              value={formData.almacen_destino || ""}
              onChange={(e) => setFormData({ ...formData, almacen_destino: e.target.value })}
            >
              <option value="">{t("almacenes.transfer.selectDestination")}</option>
              {almacenes.map((a) => (
                <option key={a.id} value={a.id}>{a.nombre}</option>
              ))}
            </Select>

            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                fullWidth
                type="number"
                label={t("almacenes.transfer.quantity")}
                variant="outlined"
                value={formData.cantidad}
                onChange={(e) => {
                  const val = e.target.value === "" ? "" : Math.max(0, Math.min(Number(e.target.value), stockDisponible));
                  setFormData({ ...formData, cantidad: val });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, max: stockDisponible }}
              />
              <span style={{ fontSize: "13px", color: "#888", whiteSpace: "nowrap" }}>
                / {stockDisponible}
              </span>
            </Box>
          </Box>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gridGap: "15px",
              fontSize: "12px",
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "whitesmoke",
                color: "#92004D",
                ":hover": { backgroundColor: "#92004D", color: "white" },
                p: 1,
              }}
              disableElevation
              onClick={handleClose}
            >
              {t("actions.cancel")}
            </Button>
            <LaeButton onClick={handleSubmit}>
              {t("almacenes.transfer.confirm")}
            </LaeButton>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default TransferirStock;
